import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { NAVIGATOR, SESSION_STORAGE } from '@ng-web-apis/common';
import { I18NextService, ITranslationService } from 'angular-i18next';
import { BehaviorSubject, Observable, Subscription, catchError, mergeMap, of, retry, throwError } from 'rxjs';
import { GoogleMapService } from 'src/app/_shared/components/google-map/google-map.service';
import { SearchService } from 'src/app/_shared/components/prefix-search/search.service';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { GoogleAnalyticsService } from '../analytics/google-analytics.service';
import { OpenDialogService } from '../open-dialog/open-dialog.service';

@Injectable({ providedIn: 'root', deps: [GoogleMapService] })
export class LocationSearchService implements OnDestroy {
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private apiLoaded: boolean;
  private apiSubscription = new Subscription();
  private geolocationSubscription = new Subscription();

  constructor(
    private routeTranslateService: RouteTranslateService,
    @Inject(I18NextService) private translateService: ITranslationService,
    private geocoder: MapGeocoder,
    private ngZone: NgZone,
    private router: Router,
    private openDialogService: OpenDialogService,
    private searchService: SearchService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private googleMapService: GoogleMapService,
    private matSnackBar: MatSnackBar,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    @Inject(NAVIGATOR) private navigator: Navigator,
  ) {}

  onSearch(types: string[] = [], searchQualifications: string[] = [], withUsers?: boolean) {
    if (this.apiLoaded) {
      this.loading.next(true);
      this.sessionStorage.removeItem('previousSearch');
      this.sessionStorage.removeItem('previousSearch__map');
      this.sessionStorage.removeItem('mobileSearchPageValue');
      let latitude: number;
      let longitude: number;
      this.geolocationSubscription = this.getLocation()
        .pipe(
          catchError((err: GeolocationPositionError) => throwError(() => err)),
          mergeMap(position => {
            latitude = position.coords.latitude;
            longitude = position.coords.longitude;
            return this.geocoder.geocode({
              location: {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              },
            });
          }),
        )
        .subscribe({
          next: response => {
            this.searchService.setSearchRefresh(true);
            this.router.navigate([this.routeTranslateService.translateRoute('search-results')], {
              queryParams: {
                prefix: 'location',
                types,
                searchQualifications,
                withUsers,
                mode: 'list',
                interaction: 'geoLocation',
                latitude,
                longitude,
                distance: 50,
                value:
                  response.results[0].address_components?.filter(
                    c =>
                      !c.types.includes('street_address') &&
                      !c.types.includes('route') &&
                      !c.types.includes('street_number') &&
                      !c.types.includes('postal_code'),
                  )[0].long_name +
                  ', ' +
                  response.results[0].address_components?.filter(c =>
                    c.types.includes('administrative_area_level_2'),
                  )[0].short_name,
              },
              state: { refresh: true },
            });
            this.ngZone.run(() => {
              this.loading.next(false);
              this.googleAnalyticsService.logCustomEvent('near_by', 'NearByBtn');
            });
          },
          error: (err: GeolocationPositionError | any) => {
            const data: GeolocationPositionError | any = { ...err };
            import('../../_shared/dialogs/location-search-dialog/location-search-dialog.component').then(
              ({ LocationSearchDialogComponent }) => {
                this.openDialogService.openDialog(LocationSearchDialogComponent, {
                  panelClass: 'sharry-alert-container',
                  data,
                });
                this.loading.next(false);
              },
            );
          },
        });
    } else {
      this.apiSubscription = this.googleMapService.apiLoaded
        .pipe(
          mergeMap(loaded => {
            if (loaded) {
              return of({ loaded, error: false });
            } else {
              return throwError(() => new Error());
            }
          }),
          retry({
            count: 5,
            delay: 500,
            resetOnSuccess: true,
          }),
          catchError(() => of({ error: true, loaded: false })),
        )
        .subscribe({
          next: r => {
            this.apiLoaded = r.loaded;
            if (r.loaded) {
              this.onSearch(types, searchQualifications, withUsers);
            } else {
              this.matSnackBar.open(this.translateService.t('location-search.error.subtitle'), '', {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              });
            }
          },
        });
      this.googleMapService.loadApi();
    }
  }

  ngOnDestroy(): void {
    this.apiSubscription.unsubscribe();
    this.geolocationSubscription.unsubscribe();
  }

  private getLocation(): Observable<GeolocationPosition> {
    return new Observable(obs => {
      if (this.navigator.geolocation) {
        this.navigator.geolocation.getCurrentPosition(
          success => {
            obs.next(success);
            obs.complete();
          },
          error => {
            obs.error(error);
          },
        );
      } else {
        obs.error('No geolocation');
      }
    });
  }
}
